import { z } from "zod";

/**
 * The source of each sign-up (website traffic plan, task 0.7, strategy repo
 * `teamna/strategy/website-traffic-plan.md`, sections 0.7 and 10).
 *
 * The owner sign-up asks one question, "How did you hear about Teamna?", with one tap. The answer,
 * the referrer name (only for "Another owner") and the website campaign values go on the business
 * row. Control Center shows them. Teamna staff can also tag a business "Warm intro" there.
 *
 * The rule: a business is a warm intro when the answer is "Anas or Lana" or the tag is on. Every
 * other business is not a warm intro.
 */

/** The answers, in the order the sign-up shows them. The values never change: reports read them. */
export const heardFromValues = ["another_owner", "parent_or_link", "google", "instagram", "founders", "other"] as const;
export const heardFromSchema = z.enum(heardFromValues);
export type HeardFrom = z.infer<typeof heardFromSchema>;

/** The words a person reads for each answer. */
export const heardFromLabels: Record<HeardFrom, string> = {
  another_owner: "Another owner",
  parent_or_link: "A parent or a Teamna link",
  google: "Google",
  instagram: "Instagram",
  founders: "Anas or Lana",
  other: "Other",
};

/**
 * The answers the sign-up shows. "Anas or Lana" is not one since 2026-10-23 (Anas): staff tag a
 * warm intro in Control Center. The value stays, because older businesses have it as their answer.
 */
export const heardFromChoices = heardFromValues.filter((value) => value !== "founders");

/** The longest referrer name the sign-up keeps. */
export const REFERRER_NAME_MAX = 120;

/**
 * One website campaign value (`utm_source`, `utm_medium`, `utm_campaign`): a short token of
 * letters, digits, `_`, `-` and `.`, at most 60 characters. The website (`site.js`) and Desk web
 * (`campaignProps`) apply the same rule, so a value that breaks it never came from Teamna.
 */
export const CAMPAIGN_TOKEN_MAX = 60;
const CAMPAIGN_TOKEN = /^[a-z0-9_.-]+$/;

/** The token in lower case, or null when it breaks the rule. A bad value is dropped, never cut. */
export function cleanCampaignToken(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const token = value.trim().toLowerCase();
  return token && token.length <= CAMPAIGN_TOKEN_MAX && CAMPAIGN_TOKEN.test(token) ? token : null;
}

/**
 * A campaign value in a request. A value that breaks the rule becomes null and does not refuse the
 * request: a bad campaign tag must never stop a person who signs up.
 */
export const campaignTokenSchema = z
  .preprocess((value) => cleanCampaignToken(value), z.string().nullable())
  .describe(
    "A website campaign value. Trimmed and made lower case. Kept only when it is 1 to 60 characters of a-z, 0-9, _, - and . Any other value is stored as null and the request does not fail.",
  );

/** The warm-intro rule (task 0.7). */
export function isWarmIntro(heardFrom: HeardFrom | null, warmIntroTag: boolean): boolean {
  return heardFrom === "founders" || warmIntroTag;
}
